import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, downloadFile } from "@/lib/api";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2,
  Clock,
  Bus,
  Truck,
  Baby,
  Users,
  MoreVertical,
  Pencil,
  RefreshCw,
  CheckCircle2,
  XCircle,
  History,
  Mail,
  Phone,
  Lock,
  Unlock,
  SlidersHorizontal,
  AlertTriangle,
  FileSpreadsheet,
  Copy,
  Download,
  Languages,
  Split,
  StickyNote,
  Trash2,
  ArrowLeftRight,
  Send,
} from "lucide-react";
import { ESTADOS_RESERVA, CANALES, TIPOS_MODALIDAD, VARIANTE_NIAGARA, idiomaAbbr } from "@/lib/constants";
import { ALERTA, formatFechaLarga } from "@/lib/agenda";
import { nombreTourDisplay } from "@/lib/tourDisplay";
import ReservaFormDialog from "@/components/reservas/ReservaFormDialog";
import CambiarModalidadDialog from "@/components/agenda/CambiarModalidadDialog";
import HistorialReservaDialog from "@/components/agenda/HistorialReservaDialog";
import SalidaConfigDialog from "@/components/agenda/SalidaConfigDialog";
import EliminarSalidaDialog from "@/components/agenda/EliminarSalidaDialog";
import AvisoCierreDialog, { cuandoSeMando } from "@/components/agenda/AvisoCierreDialog";

const ESTADOS_ACTIVOS = ["pendiente", "confirmada", "reprogramada"];

function vehiculosLabel(s) {
  if (!s) return "";
  if (s.vehiculos && s.vehiculos.length > 0) {
    const counts = {};
    for (const v of s.vehiculos) counts[v.tipo] = (counts[v.tipo] || 0) + 1;
    return Object.entries(counts)
      .map(([tipo, n]) => `${n} ${tipo === "bus" ? "Bus" : "Van"}`)
      .join(" + ");
  }
  return s.vehiculo === "van" ? "Van" : "Bus";
}

function IdiomaBadge({ idioma }) {
  const abr = idiomaAbbr(idioma);
  return (
    <span
      className={`inline-flex items-center justify-center px-1 py-0 rounded text-[9px] font-bold border leading-4 ${
        abr === "EN" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-50 text-slate-600 border-slate-200"
      }`}
      title={abr === "EN" ? "Inglés" : "Español"}
      data-testid={`pasajero-idioma-${abr.toLowerCase()}`}
    >
      {abr}
    </span>
  );
}

export function PasajeroRow({ r, onEdit, onModalidad, onPagado, onCancelar, onHistorial, onMover, miembrosGrupo }) {
  const e = ESTADOS_RESERVA[r.estado] || ESTADOS_RESERVA.pendiente;
  const canal = r.sources?.[0]?.canal || "manual";
  const cuenta = r.sources?.[0]?.cuenta;
  const saldo = Number(r.saldo_pendiente || 0);
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={`pasajero-row-${r.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-900">
              {r.pasajero?.nombre} {r.pasajero?.apellido}
            </span>
            <IdiomaBadge idioma={r.pasajero?.idioma} />
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${e.cls}`}>
              {e.label}
            </span>
          </div>
          <p className="font-mono text-[11px] text-slate-400 mt-0.5">{r.codigo_booking}</p>
          {r.codigo_pago && (
            <p className="font-mono text-[11px] text-slate-400">Pago: {r.codigo_pago}</p>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[11px] text-slate-500">
            {r.pasajero?.email && (
              <span className="inline-flex items-center gap-1">
                <Mail className="h-3 w-3" /> {r.pasajero.email}
              </span>
            )}
            {r.pasajero?.telefono && (
              <span className="inline-flex items-center gap-1">
                <Phone className="h-3 w-3" /> {r.pasajero.telefono}
              </span>
            )}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" data-testid={`pasajero-acciones-${r.id}`}>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={() => onEdit(r)} data-testid={`accion-editar-${r.id}`}>
              <Pencil className="h-4 w-4 mr-2" /> Editar
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onModalidad(r)} data-testid={`accion-modalidad-${r.id}`}>
              <RefreshCw className="h-4 w-4 mr-2" /> Cambiar modalidad
            </DropdownMenuItem>
            {onMover && miembrosGrupo && miembrosGrupo.length > 1 && (
              <DropdownMenuItem onClick={() => onMover(r)} data-testid={`accion-mover-grupo-${r.id}`}>
                <ArrowLeftRight className="h-4 w-4 mr-2" /> Mover a otra salida del grupo
              </DropdownMenuItem>
            )}
            {saldo > 0 && (
              <DropdownMenuItem onClick={() => onPagado(r)} data-testid={`accion-pagado-${r.id}`}>
                <CheckCircle2 className="h-4 w-4 mr-2" /> Marcar pagado (saldo 0)
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => onHistorial(r)} data-testid={`accion-historial-${r.id}`}>
              <History className="h-4 w-4 mr-2" /> Ver historial
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onCancelar(r)}
              className="text-red-600 focus:text-red-600"
              data-testid={`accion-cancelar-${r.id}`}
            >
              <XCircle className="h-4 w-4 mr-2" /> Cancelar reserva
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px]">
        <Badge variant="outline" className="text-[10px]">
          {TIPOS_MODALIDAD[r.modalidad] || r.modalidad}
        </Badge>
        {r.variante && VARIANTE_NIAGARA[r.variante] && (
          <Badge
            variant="outline"
            className={`text-[10px] ${VARIANTE_NIAGARA[r.variante].cls}`}
            data-testid={`pasajero-variante-${r.id}`}
          >
            {VARIANTE_NIAGARA[r.variante].label}
          </Badge>
        )}
        <span className="inline-flex items-center gap-1 text-slate-600">
          <Users className="h-3.5 w-3.5 text-slate-400" /> {r.adultos || 0} ad · {r.ninos || 0} niñ
        </span>
        {(r.infantes || 0) > 0 && (
          <span className="inline-flex items-center gap-1 text-amber-700">
            <Baby className="h-3.5 w-3.5" /> {r.infantes} inf
          </span>
        )}
        <span className="text-slate-300">·</span>
        <span className="text-slate-600">
          {CANALES[canal] || canal}
          {cuenta ? ` · ${cuenta}` : ""}
        </span>
        <span className="ml-auto">
          {saldo > 0 ? (
            <span className="text-rose-600 font-medium">Saldo ${saldo.toFixed(0)}</span>
          ) : (
            <span className="text-emerald-600 font-medium">Pagado</span>
          )}
        </span>
      </div>
    </div>
  );
}

export default function SalidaDetailSheet({ open, onOpenChange, salidaId, tours, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modalReserva, setModalReserva] = useState(null);
  const [histId, setHistId] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [separarConfirmOpen, setSepararConfirmOpen] = useState(false);
  const [eliminarOpen, setEliminarOpen] = useState(false);
  const [avisoOpen, setAvisoOpen] = useState(false); // PROMPT 134: aviso de cierre a las agencias
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!salidaId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/agenda/salida/${salidaId}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [salidaId]);

  useEffect(() => {
    if (open && salidaId) load();
  }, [open, salidaId, load]);

  const refetch = () => {
    load();
    onChanged?.();
  };

  const marcarPagado = async (r) => {
    setBusy(true);
    try {
      const total = Number(r.monto_pagado || 0) + Number(r.saldo_pendiente || 0);
      await api.post(`/reservas/${r.id}/saldo`, { saldo_pendiente: 0, monto_pagado: total });
      toast.success("Saldo puesto en cero. Reserva marcada como pagada.");
      refetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const cancelar = async (r) => {
    setBusy(true);
    try {
      await api.post(`/reservas/${r.id}/estado`, { nuevo_estado: "cancelada", motivo: "Cancelada desde la agenda" });
      toast.success("Reserva cancelada. Se liberó el cupo.");
      refetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggleCierre = async () => {
    const s = data?.salida;
    if (!s) return;
    setBusy(true);
    try {
      const accion = s.estado === "cerrada" ? "abrir" : "cerrar";
      await api.post(`/salidas/${s.id}/${accion}`);
      toast.success(accion === "cerrar" ? "Disponibilidad cerrada para esta salida." : "Disponibilidad reabierta.");
      refetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const combinarIdioma = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/salidas/${data.salida.id}/idioma`, { modo: "combinada" });
      if (res.hermana_encontrada) {
        toast.success(`Salida combinada. Se fusionaron ${res.movidas} reserva(s) de la otra salida.`);
      } else {
        toast.success("Salida marcada como combinada (bilingüe, tope 52). No había otra salida del otro idioma para fusionar.");
      }
      refetch();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const separarIdioma = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post(`/salidas/${data.salida.id}/idioma`, { modo: "separar" });
      const langs = Object.keys(res.separadas || {});
      toast.success(`Salida separada en ${langs.length} salida(s) por idioma. La configuración de vehículos se reinició.`);
      setSepararConfirmOpen(false);
      onOpenChange(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const copiarContactos = async () => {
    try {
      const { data: c } = await api.get(`/reportes/contactos/${data.salida.id}`);
      if (!c.contactos.length) return toast.info("No hay contactos activos en esta salida.");
      const texto = c.contactos
        .map((x) => `${x.nombre} — ${x.telefono || "sin teléfono"} — ${x.email || "sin email"}`)
        .join("\n");
      await navigator.clipboard.writeText(texto);
      toast.success(`${c.contactos.length} contacto(s) copiado(s) al portapapeles.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const descargarContactosCsv = async () => {
    const s2 = data.salida;
    try {
      await downloadFile(`/reportes/contactos/${s2.id}/csv`, `contactos_${s2.fecha}_${(s2.hora || "").replace(":", "")}.csv`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const descargarLista = async () => {
    const s2 = data.salida;
    try {
      await downloadFile(`/reportes/salida/${s2.id}/xlsx`, `lista_embarque_${s2.fecha}_${(s2.hora || "").replace(":", "")}.xlsx`);
      toast.success("Lista de embarque descargada.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const s = data?.salida;
  const a = s ? ALERTA[s.alerta] || ALERTA.verde : ALERTA.verde;
  const VehiculoIcon = s?.vehiculo === "van" ? Truck : Bus;
  const reservas = data?.reservas || [];
  const activas = reservas.filter((r) => ESTADOS_ACTIVOS.includes(r.estado) && !r.requiere_revision);
  const inactivas = reservas.filter((r) => !ESTADOS_ACTIVOS.includes(r.estado) || r.requiere_revision);

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="salida-detail-sheet">
          {loading || !s ? (
            <div className="flex flex-col h-full">
              <SheetHeader className="sr-only">
                <SheetTitle>Detalle de salida</SheetTitle>
                <SheetDescription>Cargando información de la salida.</SheetDescription>
              </SheetHeader>
              <div className="flex items-center justify-center flex-1">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            </div>
          ) : (
            <div>
              {/* Header */}
              <div className="sticky top-0 z-10 bg-white border-b border-slate-200 p-5">
                <SheetHeader className="text-left space-y-0">
                  <div className="flex items-center gap-2 text-slate-500 text-sm">
                    <Clock className="h-4 w-4" />
                    <span className="font-bold text-slate-900 text-base tabular-nums">{s.hora}</span>
                    <span className="capitalize">{formatFechaLarga(s.fecha)}</span>
                  </div>
                  <SheetTitle className="font-heading text-xl mt-1">{nombreTourDisplay(s.tour_nombre, s.modalidad)}</SheetTitle>
                  <SheetDescription asChild>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                      <Badge variant="outline" className="text-[10px]">
                        {TIPOS_MODALIDAD[s.modalidad] || s.modalidad}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] gap-1" data-testid="detalle-vehiculos">
                        <VehiculoIcon className="h-3 w-3" /> {vehiculosLabel(s)}
                      </Badge>
                      <Badge
                        variant="outline"
                        className={`text-[10px] uppercase ${s.idioma === "combinada" ? "border-indigo-200 text-indigo-700 bg-indigo-50" : ""}`}
                        data-testid="detalle-idioma"
                      >
                        {s.idioma === "combinada" ? "Combinada" : s.idioma === "en" ? "Inglés" : "Español"}
                      </Badge>
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${a.chip}`}
                      >
                        {s.alerta === "rojo" && <AlertTriangle className="h-3 w-3" />}
                        {s.alerta === "cerrada" && <Lock className="h-3 w-3" />}
                        {a.label}
                      </span>
                    </div>
                  </SheetDescription>
                  {s.nota && (
                    <p className="flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 mt-2" data-testid="detalle-nota">
                      <StickyNote className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {s.nota}
                    </p>
                  )}
                </SheetHeader>

                {/* Ocupación */}
                <div className="mt-4">
                  <div className="flex items-end justify-between mb-1">
                    <span className="text-xs text-slate-500">Ocupación (respeta regla de infantes)</span>
                    <span className={`text-sm font-bold tabular-nums ${a.text}`} data-testid="detalle-ocupacion">
                      {s.cupo_usado} / {s.cupo_max || "—"}
                    </span>
                  </div>
                  <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full ${a.bar} transition-all duration-500`}
                      style={{ width: `${s.cupo_max ? Math.min(100, (s.cupo_usado / s.cupo_max) * 100) : 0}%` }}
                    />
                  </div>
                  {s.idioma === "combinada" && s.idioma_breakdown && (
                    <p className="text-[11px] text-indigo-600 mt-1.5" data-testid="detalle-idioma-breakdown">
                      <Languages className="h-3.5 w-3.5 inline mr-1" />
                      {s.idioma_breakdown.en} EN + {s.idioma_breakdown.es} ES
                    </p>
                  )}
                  <p className="text-[11px] text-slate-500 mt-1.5 inline-flex items-center gap-1">
                    <Baby className="h-3.5 w-3.5" />
                    {data.infantes_total} infante(s) ·{" "}
                    {s.infante_cuenta ? (
                      <span className="text-amber-700 font-medium">ocupan asiento en esta salida</span>
                    ) : (
                      <span className="text-slate-500">no ocupan asiento (bus regular)</span>
                    )}
                  </p>
                </div>

                {/* Estado de disponibilidad en Bokun (cierre/reapertura automática) */}
                {(() => {
                  const bc = s.bokun_closeout;
                  if (!bc) return null;
                  const map = {
                    api: { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: <Lock className="h-3 w-3" />, txt: "Disponibilidad cerrada en Bokun" },
                    parcial: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: <AlertTriangle className="h-3 w-3" />, txt: "Cerrada parcialmente en Bokun (revisar)" },
                    pendiente_credenciales: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: <AlertTriangle className="h-3 w-3" />, txt: "Cierre pendiente · cargá credenciales de Bokun" },
                    sin_mapeo: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: <AlertTriangle className="h-3 w-3" />, txt: "Sin producto Bokun mapeado · cerrá las OTAs a mano" },
                    error: { cls: "bg-rose-50 text-rose-700 border-rose-200", icon: <XCircle className="h-3 w-3" />, txt: "Error al cerrar en Bokun (reintentar)" },
                    // PROMPT 140: nada se toco en Bokun (interruptor apagado, o un resguardo lo freno al cerrar)
                    desligado: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: <AlertTriangle className="h-3 w-3" />, txt: "Bókun no se tocó: la conexión de disponibilidad está apagada · cerrá o abrí a mano" },
                    no_cerrado: { cls: "bg-amber-50 text-amber-700 border-amber-200", icon: <AlertTriangle className="h-3 w-3" />, txt: "No se cerró en Bókun: el mapeo no tiene el horario u otra salida a esa hora tiene lugar · revisá a mano" },
                  };
                  const reabierto = { cls: "bg-slate-50 text-slate-600 border-slate-200", icon: <Unlock className="h-3 w-3" />, txt: "Disponibilidad reabierta en Bokun" };
                  const cfg = bc.modo === "desligado" || bc.modo === "no_cerrado"
                    ? map[bc.modo]
                    : bc.cerrado ? (map[bc.modo] || map.error) : reabierto;
                  return (
                    <div className="mt-3">
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${cfg.cls}`}
                        data-testid="bokun-closeout-status"
                      >
                        {cfg.icon} {cfg.txt}
                      </span>
                    </div>
                  );
                })()}

                {/* PROMPT 134: el ultimo aviso de cierre que se les mando a las agencias */}
                {s.aviso_cierre && (
                  <div className="mt-2">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-medium ${
                        s.aviso_cierre.fallidos?.length
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-sky-50 text-sky-700 border-sky-200"
                      }`}
                      data-testid="aviso-cierre-estado"
                    >
                      <Send className="h-3 w-3" /> Aviso de cierre: {cuandoSeMando(s.aviso_cierre.enviado_en)} ·{" "}
                      {s.aviso_cierre.agencias?.length || 0} agencia(s) · {s.aviso_cierre.enviados}/{s.aviso_cierre.total}{" "}
                      correo(s)
                    </span>
                  </div>
                )}

                {/* Acciones de salida */}
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleCierre}
                    disabled={busy}
                    className="gap-1.5"
                    data-testid="toggle-cierre-salida"
                  >
                    {s.estado === "cerrada" ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                    {s.estado === "cerrada" ? "Reabrir disponibilidad" : "Cerrar disponibilidad"}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setAvisoOpen(true)}
                    disabled={busy}
                    className={`gap-1.5 ${
                      s.estado === "cerrada" && !s.aviso_cierre
                        ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                        : ""
                    }`}
                    data-testid="aviso-cierre-btn"
                  >
                    <Send className="h-4 w-4" /> Avisar el cierre a las agencias
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfigOpen(true)}
                    className="gap-1.5"
                    data-testid="configurar-salida-btn"
                  >
                    <SlidersHorizontal className="h-4 w-4" /> Configurar vehículos y cupo
                  </Button>
                  {s.idioma === "combinada" ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSepararConfirmOpen(true)}
                      disabled={busy}
                      className="gap-1.5"
                      data-testid="separar-idioma-btn"
                    >
                      <Split className="h-4 w-4" /> Separar por idioma
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={combinarIdioma}
                      disabled={busy}
                      className="gap-1.5"
                      data-testid="combinar-idioma-btn"
                    >
                      <Languages className="h-4 w-4" /> Combinar bilingüe (ES+EN)
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEliminarOpen(true)}
                    disabled={busy}
                    className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                    data-testid="eliminar-salida-btn"
                  >
                    <Trash2 className="h-4 w-4" /> Eliminar salida
                  </Button>
                </div>

                {/* Base de contactos / exportación */}
                <div className="flex flex-wrap gap-2 mt-2 pt-3 border-t border-slate-100">
                  <span className="w-full text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    Base de contactos · exportar
                  </span>
                  <Button variant="outline" size="sm" onClick={copiarContactos} className="gap-1.5" data-testid="copiar-contactos-btn">
                    <Copy className="h-4 w-4" /> Copiar contactos
                  </Button>
                  <Button variant="outline" size="sm" onClick={descargarContactosCsv} className="gap-1.5" data-testid="contactos-csv-btn">
                    <Download className="h-4 w-4" /> Contactos CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={descargarLista} className="gap-1.5" data-testid="lista-embarque-btn">
                    <FileSpreadsheet className="h-4 w-4" /> Lista de embarque
                  </Button>
                </div>
              </div>

              {/* Pasajeros */}
              <div className="p-5 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                  Pasajeros · {activas.length} reserva(s) activa(s)
                </p>
                {activas.length === 0 ? (
                  <p className="text-sm text-slate-400 py-6 text-center">Sin reservas activas en esta salida.</p>
                ) : (
                  activas.map((r) => (
                    <PasajeroRow
                      key={r.id}
                      r={r}
                      onEdit={setEditing}
                      onModalidad={setModalReserva}
                      onPagado={marcarPagado}
                      onCancelar={cancelar}
                      onHistorial={(rr) => setHistId(rr.id)}
                    />
                  ))
                )}

                {inactivas.length > 0 && (
                  <div className="pt-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                      Canceladas / a revisar ({inactivas.length})
                    </p>
                    <div className="space-y-2 opacity-70">
                      {inactivas.map((r) => (
                        <PasajeroRow
                          key={r.id}
                          r={r}
                          onEdit={setEditing}
                          onModalidad={setModalReserva}
                          onPagado={marcarPagado}
                          onCancelar={cancelar}
                          onHistorial={(rr) => setHistId(rr.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Editar reserva */}
      <ReservaFormDialog
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        tours={tours}
        reserva={editing}
        onSaved={refetch}
      />

      {/* Cambiar modalidad */}
      <CambiarModalidadDialog
        open={!!modalReserva}
        onOpenChange={(v) => !v && setModalReserva(null)}
        reserva={modalReserva}
        onChanged={refetch}
      />

      {/* Historial */}
      <HistorialReservaDialog open={!!histId} onOpenChange={(v) => !v && setHistId(null)} reservaId={histId} />

      {/* Configurar vehículos y cupo */}
      <SalidaConfigDialog
        open={configOpen}
        onOpenChange={setConfigOpen}
        salida={s}
        tour={tours.find((t) => t.id === s?.tour_id)}
        onSaved={refetch}
      />

      {/* Confirmar separación por idioma */}
      <Dialog open={separarConfirmOpen} onOpenChange={setSepararConfirmOpen}>
        <DialogContent className="max-w-sm" data-testid="separar-idioma-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Separar por idioma</DialogTitle>
            <DialogDescription>
              Vas a dividir esta salida combinada en 2 salidas (Español e Inglés) según el idioma de cada
              pasajero, con el cupo de catálogo (54 c/u). <strong>La configuración de vehículos y la nota
              de esta salida se van a reiniciar</strong> — vas a tener que volver a cargarla si hace falta.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSepararConfirmOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={separarIdioma} disabled={busy} data-testid="separar-idioma-confirm-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sí, separar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PROMPT 134: avisar el cierre a las agencias */}
      <AvisoCierreDialog open={avisoOpen} onOpenChange={setAvisoOpen} salida={s} onSent={refetch} />

      {/* Eliminar salida (a mano, con opción de mover reservas) */}
      <EliminarSalidaDialog
        open={eliminarOpen}
        onOpenChange={setEliminarOpen}
        salida={s}
        reservasActivas={activas.length}
        onDeleted={() => {
          onOpenChange(false);
          onChanged?.();
        }}
      />
    </>
  );
}
